// Utility functions for provider type display logic

import { ProviderType } from '../lib/supabase';

interface ProviderWithSalutation {
  type: ProviderType;
  salutation?: string;
}

/**
 * Gets the display label for a provider type, considering gender-specific salutation
 * @param type - The provider type
 * @param salutation - The salutation (Herr/Frau) - optional
 * @returns The appropriate display label
 */
export const getProviderTypeLabel = (type: ProviderType, salutation?: string): string => {
  // Gender-specific logic for doctor type
  if (type === 'doctor') {
    if (salutation === 'Herr') {
      return 'Arzt';
    } else if (salutation === 'Frau') {
      return 'Ärztin';
    } else if (salutation === 'Divers') {
      return 'Arzt / Ärztin';
    }
    // Default fallback for doctor without salutation
    return 'Arzt / Ärztin';
  }

  // Gender-specific logic for naturopath type
  if (type === 'naturopath') {
    if (salutation === 'Herr') {
      return 'Heilpraktiker';
    } else if (salutation === 'Frau') {
      return 'Heilpraktikerin';
    } else if (salutation === 'Divers') {
      return 'Heilpraktiker / Heilpraktikerin';
    }
    // Default fallback for naturopath without salutation
    return 'Heilpraktiker / Heilpraktikerin';
  }

  // Default labels for other types (no gender-specific changes)
  const typeLabels: Record<ProviderType, string> = {
    'doctor': 'Arzt / Ärztin',
    'group_practice': 'Gemeinschaftspraxis',
    'clinic': 'Klinik / Krankenhaus',
    'naturopath': 'Heilpraktiker / Heilpraktikerin',
    'psychotherapist': 'Psychotherapeut / Psychotherapeutin',
    'therapy_practice': 'Therapeutische Praxis',
    'ambulanz': 'Ambulantes OP-Zentrum / Tagesklinik',
    'fachklinik': 'Fachklinik / Spezialklinik',
    'labor_diagnostik': 'Labor / Diagnostikzentrum',
    'mvz': 'MVZ (Medizinisches Versorgungszentrum)',
    'naturheilpraxis': 'Naturheilpraxis / Komplementärmedizinisches Zentrum',
    'pflegedienst': 'Pflegedienst / Pflegeeinrichtung',
    'reha': 'Rehaklinik / Rehabilitationszentrum',
    'dentist': 'Zahnärztliche Einrichtung',
    'sonstiges': 'Sonstiges'
  };

  return typeLabels[type] || 'Anbieter';
};

/**
 * Gets the display label for a provider considering their salutation
 * @param provider - Provider object with type and salutation
 * @returns The appropriate display label
 */
const getProviderDisplayLabel = (provider: ProviderWithSalutation): string => {
  return getProviderTypeLabel(provider.type, provider.salutation);
};